import React, { useCallback, useMemo, useState } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { Calendar } from 'lucide-react'
import { useSessionStore } from '@/stores/useSessionStore'
import { FulfillmentType } from '@/constants'
import Modal from '@/components/ui/Modal'
import { cn } from '@utils'
import { formatPhoneNumber } from '@/utils/fieldTypes'

interface Field {
  name: string
  type?: string
  min?: string
  max?: string
  default?: string | number
  label?: string
}

interface InputFieldProps {
  field: Field
  item?: any
}

const InputField = ({ field, item }: InputFieldProps) => {
  const sessionStore = useSessionStore()
  const session = sessionStore.session
  const { control, register, watch, setValue } = useFormContext<Record<string, any>>()

  const [showCalendar, setShowCalendar] = useState(false)

  const selectedItem = useMemo(() => item || session?.selected_item, [item, session?.selected_item])
  const value = watch(field.name)

  const beforeStartDate = useMemo(() => {
    if (field.name !== 'next_bill_date') {
      return null
    }
    let dt = new Date()
    if (field.min) {
      dt = new Date(field.min)
    } else {
      const current = value ? new Date(value) : new Date()
      const today = dt.getDate()
      dt.setDate(current.getDate() > today ? today + 1 : today)
    }
    dt.setHours(0, 0, 0, 0)
    return dt
  }, [field.min, field.name, value])

  const afterEndDate = useMemo(() => {
    if (field.name !== 'next_bill_date') {
      return null
    }
    let dt = new Date()
    if (field.max) {
      dt = new Date(field.max)
      dt.setDate(dt.getDate() + 1)
    } else {
      dt.setMonth(dt.getMonth() + 1)
    }
    dt.setHours(0, 0, 0, 0)
    return dt
  }, [field.max, field.name])

  const dateString = useMemo(() => {
    if (field.name !== 'next_bill_date' || !value) {
      return ''
    }
    const dt = new Date(value)
    // Explicit MM/DD/YYYY — toLocaleDateString() leaks the browser locale and
    // can mismatch the ISO storage format depending on user region.
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(dt)
  }, [field.name, value])

  const nextBillDateLabel = useMemo(() => {
    if (field.name !== 'next_bill_date') {
      return ''
    }
    return selectedItem && selectedItem.product?.fulfillment_type === FulfillmentType.NoShipping
      ? 'Next Scheduled Bill Date:'
      : 'Next Scheduled Ship Date:'
  }, [field.name, selectedItem])

  const handleDateSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const dateValue = event.target.value
      if (dateValue) {
        const date = new Date(dateValue)
        setValue(field.name, date.toISOString(), { shouldValidate: true, shouldDirty: true })
        setShowCalendar(false)
      }
    },
    [field.name, setValue]
  )

  if (field.name === 'quantity') {
    const maxQuantity = selectedItem?.campaign_product?.max_quantity || 10
    const options = Array.from({ length: maxQuantity }, (_, i) => i + 1)

    return (
      <div className="form-group">
        <label htmlFor="quantity" className="form-label">
          Quantity:
        </label>
        <Controller
          control={control}
          name={field.name}
          render={({ field: ctrl }) => (
            <select
              id="quantity"
              className="form-select"
              value={ctrl.value ?? ''}
              onChange={e => ctrl.onChange(e.target.value ? parseInt(e.target.value) : '')}>
              <option value="">Select quantity</option>
              {options.map(num => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
          )}
        />
      </div>
    )
  }

  if (field.name === 'next_bill_date') {
    const selectDay = (day: Date) => {
      setValue(field.name, day.toISOString(), { shouldValidate: true, shouldDirty: true })
      setShowCalendar(false)
    }

    const today = beforeStartDate || new Date()
    const endDate = afterEndDate || new Date()
    const selectedDate = value ? new Date(value) : null

    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
    const days: Array<Date | null> = []
    for (let i = 0; i < monthStart.getDay(); i++) {
      days.push(null)
    }
    const iter = new Date(monthStart)
    while (iter.getMonth() === monthStart.getMonth()) {
      days.push(new Date(iter))
      iter.setDate(iter.getDate() + 1)
    }

    const monthLabel = monthStart.toLocaleString(undefined, { month: 'long', year: 'numeric' })

    return (
      <div className="form-group">
        <label htmlFor="next_bill_date" className="form-label">
          {nextBillDateLabel}
        </label>

        <Modal isOpen={showCalendar} onClose={() => setShowCalendar(false)} size="sm" title={monthLabel}>
          <div className="grid grid-cols-7 gap-1 text-center mb-2">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <div
                key={`dow-${i}`}
                className="text-fluid-sm font-medium py-1"
                style={{ color: 'var(--light-text-color)' }}>
                {d}
              </div>
            ))}
            {days.map((day, i) => {
              if (!day) {
                return <div key={`empty-${i}`} />
              }
              const disabled = day < today || day > endDate
              const isSelected = !!selectedDate && day.toDateString() === selectedDate.toDateString()
              return (
                <button
                  key={`day-${i}`}
                  type="button"
                  disabled={disabled}
                  onClick={() => selectDay(day)}
                  className={cn(
                    'h-10 rounded-md text-fluid-sm transition-colors',
                    disabled && 'opacity-40 cursor-not-allowed',
                    isSelected && 'font-semibold text-white',
                    !isSelected && !disabled && 'hover:bg-black/5'
                  )}
                  style={isSelected ? { backgroundColor: 'var(--theme-color)' } : { color: 'var(--text-color)' }}>
                  {day.getDate()}
                </button>
              )
            })}
          </div>
          <div className="mt-2">
            <input
              type="date"
              className="form-control"
              min={beforeStartDate?.toISOString().split('T')[0]}
              max={afterEndDate?.toISOString().split('T')[0]}
              onChange={handleDateSelect}
            />
          </div>
        </Modal>

        <div className="relative">
          <input
            type="text"
            className="form-control"
            value={dateString}
            readOnly
            onClick={() => setShowCalendar(true)}
            placeholder="Please select a date"
          />
          <button
            type="button"
            className="btn-bare absolute inset-y-0 right-0 pr-3 cursor-pointer z-10 flex items-center"
            onClick={() => setShowCalendar(true)}
            aria-label="Open date picker">
            <Calendar size={18} />
          </button>
        </div>
      </div>
    )
  }

  const isPhone = field.type === 'phone' || field.type === 'tel'

  if (isPhone) {
    return (
      <div className="form-group">
        {field.label && (
          <label htmlFor={field.name} className="form-label">
            {field.label}
          </label>
        )}
        <Controller
          name={field.name}
          control={control}
          render={({ field: ctrl }) => (
            <input
              id={field.name}
              type="tel"
              className="form-control"
              placeholder={field.label}
              value={ctrl.value || ''}
              onChange={e => ctrl.onChange(formatPhoneNumber(e.target.value))}
              onBlur={ctrl.onBlur}
              ref={ctrl.ref}
            />
          )}
        />
      </div>
    )
  }

  return (
    <div className="form-group">
      {field.label && (
        <label htmlFor={field.name} className="form-label">
          {field.label}
        </label>
      )}
      <input
        id={field.name}
        type={field.type || 'text'}
        className="form-control"
        placeholder={field.label}
        {...register(field.name)}
      />
    </div>
  )
}

export default InputField
